import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './auth-context';
import { getCurrentUserRole } from './db-storage';
import { isSuperAdminEmailClient } from './super-admin-emails';

/**
 * After we've resolved admin role once for this user id, re-fetches run in the background
 * without setting loading=true. That keeps AdminRoute from swapping in a full-screen loader
 * when the tab regains focus and Supabase refreshes the access token (accessToken string changes).
 */
export function useAdminRole(): { isAdmin: boolean; role: string; loading: boolean } {
  const { user, accessToken } = useAuth();
  const lastResolvedUserIdRef = useRef<string | null>(null);
  const [state, setState] = useState<{ isAdmin: boolean; role: string; loading: boolean }>({
    isAdmin: false,
    role: 'user',
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!user || !accessToken) {
      lastResolvedUserIdRef.current = null;
      setState({ isAdmin: false, role: 'user', loading: false });
      return;
    }

    const sameUserFollowUp = lastResolvedUserIdRef.current === user.id;
    if (!sameUserFollowUp) {
      setState((s) => ({ ...s, loading: true }));
    }

    const { role, isAdmin, source } = await getCurrentUserRole(accessToken);
    const owner = isSuperAdminEmailClient(user.email);
    // Security hardening: fail closed for admin privilege if role API lookup fails.
    // Only hard-coded super admin owner fallback is allowed when source !== api.
    const resolvedRole = owner
      ? 'super_admin'
      : source === 'api'
        ? role
        : 'user';
    const resolvedAdmin = owner || (source === 'api' ? isAdmin : false);
    lastResolvedUserIdRef.current = user.id;
    setState({
      role: resolvedRole,
      isAdmin: resolvedAdmin,
      loading: false,
    });
  }, [user, accessToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return state;
}
